<template>
  <div class="mt-3">
    <form ref="form">
      <v-row>
        <v-select
          :items="templates"
          item-text="translatedName"
          item-value="id"
          :menu-props="{bottom: true, offsetY: true}"
          v-model="selectedTemplate"
          outlined
          clearable
          :label="$t('display/editor:left-panel.templates')"
        />
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            class="full-width"
            color="primary"
            @click="loadTemplate"
          >
            {{ $t('display/editor:left-panel.load') }}
          </v-btn>
        </v-col>
        <v-col>
          <TwoButtonDialog
            btn-class="full-width"
            ref="deleteButton"
            color="primary"
            :btn-text="$t('buttons:delete')"
            :hideOverlay="true"
            :rounded="false"
            :dialog-title="$t('dialog:delete-btn-title', {name : $t('display/editor:left-panel.delete-title')})"
            :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('display/editor:left-panel.delete-text')})"
            :button1-text="$t('buttons:cancel')"
            :button2-text="$t('buttons:delete')"
            @clickedButton1="$refs.deleteButton.showDialog = false"
            @clickedButton2="deleteEditor()"
          />
        </v-col>
      </v-row>
      <v-row>
        <h3 class="primary--text font-weight-bold pt-5">
          {{ $t('display/editor:left-panel.name-of-the-content') }}
        </h3>
        <v-text-field
          class="full-width"
          outlined
          clearable
          v-model="currentTemplate.name"
          :label="$t('common:name')"
        />
      </v-row>
      <v-row>
        <v-col>
          <DateField
            ref="dateStart"
            :disableMask="true"
            :disabled="buttonType === 0"
            @update="(v) => {currentTemplate.dateStart = v; $refs.dateEnd.setMinDate(v)}"
            :isClearable="true"
            :label="$t('common:start-date')"
            :rules="[ruleRequired]"
          />
        </v-col>
        <v-col>
          <DateField
            ref="dateEnd"
            :disableMask="true"
            :disabled="buttonType === 0"
            @update="(v) => {currentTemplate.dateEnd = v; $refs.dateStart.setMaxDate(v)}"
            :isClearable="true"
            :label="$t('common:end-date')"
          />
        </v-col>
      </v-row>
      <div>
        <v-item-group
          class="advert-type"
          v-model="buttonType"
          mandatory>
          <v-container>
            <v-row dense>
              <v-col
                v-for="n in 2"
                :key="n"
                cols="12"
                md="6"
              >
                <v-item v-slot="{ active, toggle }">

                  <v-card
                    :color="active ? 'primary' : ''"
                    class="d-flex align-center"
                    height="150"
                    @click="toggle"
                  >
                    <v-scroll-y-transition>
                      <div
                        class="flex-grow-1 text-center"
                      >
                        <v-icon x-large>{{ n === 1 ? 'smart_button' : 'badge' }}</v-icon>
                        {{
                          n === 1
                            ? $t('display/editor:left-panel.button-content')
                            : $t('display/editor:left-panel.content')
                        }}
                      </div>
                    </v-scroll-y-transition>
                  </v-card>
                </v-item>
              </v-col>
            </v-row>
          </v-container>
        </v-item-group>
      </div>
      <v-row>
        <v-btn-toggle
          mandatory
          class="ml-3 mt-3"
          v-model="side">
          <v-btn
            :disabled="buttonType != 0">
            <v-icon>format_align_left</v-icon>
          </v-btn>
          <v-btn
            :disabled="buttonType != 0">
            <v-icon>format_align_right</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-row>
      <v-row v-if="!isAdmin">
        <AdditionalElevator
            ref="additionalElevator"
            @elevatorIsLoaded="$refs.additionalElevator.elevatorLoaded()"
            @updateElevators="e => elevatorIds = e"
          />
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            color="primary"
            class="full-width"
            @click="$emit('saveAdvert')"
          >
            {{ $t('buttons:save') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn
            class="full-width"
            @click="$emit('publishAdvert')">
            {{ $t('display/editor:left-panel.publish') }}
          </v-btn>
        </v-col>
      </v-row>
    </form>

  </div>
</template>

<script>
import DateUtils from '@/modules/core/services/date-utils';
import DateField from '@/modules/core/components/DateField';
import EditorService from '@/modules/display/services/editor-service';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import Utils from '@/modules/core/services/utils';
import {Role} from '@/modules/core/data/roles';
import AdditionalElevator from "@/modules/display/components/AdditionalElevator";

export default {
  name: 'LeftPanel',
  components: {
    AdditionalElevator,
    DateField,
    TwoButtonDialog
  },
  data() {
    return {
      templates: [],
      rawTemplates: [],
      elevatorIds: [],
      currentTemplate: {},
      selectedTemplate: null,
      buttonType: 0,
      side: 0
    }
  },
  computed: {
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Admin);
      }
      return false;
    },

    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  watch: {
    buttonType() {
      this.$emit('updateType', this.buttonType === 0);
    },
    side() {
      this.$emit('updateSide', this.side === 0);
    }
  },
  methods: {
    loadTemplate() {
      let selectedTemplateBeforeReset = this.selectedTemplate;
      if (!selectedTemplateBeforeReset) {
        return;
      }

      this.currentTemplate = Object.assign({}, this.getTemplateById(selectedTemplateBeforeReset));

      let dateStart = DateUtils.parseStringToDate(this.currentTemplate.dateStart);
      let dateEnd = DateUtils.parseStringToDate(this.currentTemplate.dateEnd);

      if (dateStart > dateEnd) {
        this.currentTemplate.dateEnd = this.currentTemplate.dateStart;
      }

      if (this.currentTemplate.dateStart !== null) {
        this.currentTemplate.dateStart = this.$refs.dateStart.parseDTO(this.currentTemplate.dateStart);
        this.$refs.dateEnd.setMinDate(this.currentTemplate.dateStart);
      }

      if (this.currentTemplate.dateEnd !== null) {
        this.currentTemplate.dateEnd = this.$refs.dateEnd.parseDTO(this.currentTemplate.dateEnd);
        this.$refs.dateStart.setMaxDate(this.currentTemplate.dateEnd);
      }

      if (this.currentTemplate.buttonText) {
        this.$emit('updateButtonText', this.currentTemplate.buttonText);
      }

      this.buttonType = this.currentTemplate.button === true ? 0 : 1;
      this.side = this.currentTemplate.leftButton === true ? 0 : 1;

      this.loadHtmlText(selectedTemplateBeforeReset);
      this.selectedTemplate = selectedTemplateBeforeReset;
    },
    getTemplateById(id) {
      let result = {};

      for (let i = 0; i < this.rawTemplates.length && id; i++) {
        if (this.rawTemplates[i].id === id) {
          result = this.rawTemplates[i];
          break;
        }
      }

      return result;
    },
    readAllTemplates() {
      this.rawTemplates = [];
      this.templates = [];
      EditorService.readAllFromAPI(
        (response) => {
          this.rawTemplates = response.data;
          response.data.forEach(
            (template) => {
              this.templates.push({
                id: template.id,
                translatedName: template.name
              });
            }
          )
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('display/editor:left-panel.templates')}));
        }
      )
    },
    setDefaultValues() {
      this.currentTemplate = {};
      this.selectedTemplate = null;

      let today = new Date();
      let dayStart = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;
      today = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      let dayEnd = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;

      if (typeof this.$refs.dateStart.parseDTO !== 'undefined') {
        this.currentTemplate.dateStart = this.$refs.dateStart.parseDTO(dayStart);
        this.currentTemplate.dateEnd = this.$refs.dateEnd.parseDTO(dayEnd);
      }
    },
    loadHtmlText(id) {
      EditorService.readTemplateHtmlById(id,
        (response) => {
          this.$emit('editorTextUpdate', response.data);
        }
      )
    },
    getPublishData() {
      return {
        id: this.selectedTemplate ? this.selectedTemplate : null,
        customerId: this.currentTemplate.customerId ? this.currentTemplate.customerId : null,
        name: this.currentTemplate.name ? this.currentTemplate.name : '',
        dateStart: this.currentTemplate.dateStart,
        dateEnd: this.currentTemplate.dateEnd,
        elevatorIds: this.elevatorIds
      }
    },
    updatePanel(data) {
      this.readAllTemplates();
      this.selectedTemplate = data.id;
    },
    deleteEditor() {
      if (!this.selectedTemplate) return;

      EditorService.deleteTemplate(
        this.selectedTemplate,
        () => {
          this.readAllTemplates();
          this.setDefaultValues();
          this.$emit('editorTextUpdate', '');
          this.$emit('updateButtonText', this.$t('display/editor:editor-panel.insert-your-text'));
          this.$refs.deleteButton.showDialog = false;
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('display/editor:left-panel.template')}));
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('display/editor:left-panel.template')}));
        }
      )
    },
    ruleRequired(text) {
      return v => (v === 0 || !!v) || text;
    }
  },
  mounted() {
    this.readAllTemplates();
    this.setDefaultValues();
  }
}
</script>

<style scoped>
 .advert-type .container {
   padding: 0;
 }
</style>
<style>
  .full-width {
    width: 100%;
  }
</style>